/**
 * Design tokens — single source of truth for brand colours.
 * =========================================================
 *
 * Why this file exists
 * --------------------
 * The codebase historically hardcoded brand hexes inline in dozens of files,
 * with many near-duplicate clusters (the *same* intended colour written a
 * channel or two apart, e.g. `#02c995` / `#01c996` / `#04c898`). That made the
 * palette impossible to reason about or restyle. This file (and its CSS mirror
 * `src/styles/tokens.css`) is now the ONE place brand colours are defined.
 *
 * Use this file in TS / MUI `sx` / `style` props where a CSS variable is not
 * ergonomic (objects, ternaries, theme palette). Use the CSS custom properties
 * from `tokens.css` (`var(--color-brand-green)` …) in `.scss`/`.css` and in
 * styled-component template strings.
 *
 * The MUI theme (`theme.tsx`) is wired to these same constants so MUI
 * components and raw-hex usages share one source of truth.
 *
 * ---------------------------------------------------------------------------
 * Consolidation map  (raw hex  ->  canonical token)
 * ---------------------------------------------------------------------------
 * Near-duplicate clusters were collapsed to the MOST-FREQUENT hex in each
 * cluster. Each collapse moves a colour by <= 2 / 255 per channel — an
 * imperceptible change; that is the point of tokenizing.
 *
 *  BRAND GREEN -> tokens.brandGreen   #02c995   (canonical, 45 uses)
 *      #01c996 (28)   -> #02c995   (+1,+0,-1)
 *      #04c898 (2)    -> #02c995   (-2,+1,-3)
 *      #01c99626 / #04c89826 / #01c9963d  -> brandGreen + same alpha suffix
 *
 *  BRAND YELLOW -> tokens.brandYellow  #fbd82c  (canonical, 28 uses)
 *      #fbd82b (20)   -> #fbd82c   (0,0,+1)
 *      #fbdd49 (4)    -> #fbd82c   (0,-5,-29)  *see note
 *      #fbde48 (2)    -> #fbd82c   (0,-6,-28)  *see note
 *      #fbdd4926      -> brandYellow + alpha
 *   *note: #fbdd49 / #fbde48 are a slightly lighter "approval" yellow. They are
 *    just over the 2/255 threshold on the blue channel, but were the SAME
 *    intended brand yellow used interchangeably with #fbd82b in sibling status
 *    maps (helper.ts status_color vs getApprovalStatusColor). Consolidated to
 *    the canonical brand yellow per the cluster brief. If a distinct lighter
 *    approval-yellow is ever wanted, reintroduce it as `brandYellowLight`.
 *
 *  BRAND BLUE -> tokens.brandBlue   #0e46bc  (canonical, 20 uses; == theme primary #0D46BC)
 *      #0947bf (6)    -> #0e46bc   (+5,-1,-3)
 *      #0946c3 (2)    -> #0e46bc   (+5,0,-7)
 *      #0d45bc (1)    -> #0e46bc   (+1,+1,0)   (ACCENT_COLORS entry)
 *      #0e47bf (1)    -> #0e46bc   (0,-1,-3)
 *      #0947bf26 / #0946c326  -> brandBlue + alpha
 *      (theme primary was literal #0D46BC -> now tokens.brandBlue)
 *
 *  ERROR / REJECTED RED -> tokens.error   #ed1111  (canonical, app's bespoke red, 19 uses)
 *      #ed111126      -> error + alpha
 *   (kept distinct: tokens.errorMaterial #f44336 — MUI's material red used by
 *    the theme `error.main` palette + 2 component spots. Not a near-dupe of
 *    #ed1111, so NOT consolidated — folding it in would be a perceptible shift
 *    on MUI error components.)
 *
 *  INFO BLUE -> tokens.info   #009cfe  (canonical, helper status info, 3 uses)
 *      #009cfe26      -> info + alpha
 *   (kept distinct, NOT near-dupes of #009cfe — different intents/shades:
 *      tokens.infoSky   #3cc1ff  — `colors.primary`, HIGHLIGHT_COLORS, appearance
 *      tokens.infoBlue  #019afc  — appearance-theme alt info)
 *
 * Neutrals / secondary / warning / soft tints are exact (no consolidation):
 *   black #000000, white #ffffff, grayText #888888, grayBorder #d8d8d8,
 *   bgLight #f7f7f7, secondary #bca700, warningGold #b8860b, warning #ff9800,
 *   warningYellow #f1c40f, softRed #ffc5c2, softRedTint #fad2d2, softGreen #ddf5d0.
 *
 * ---------------------------------------------------------------------------
 * Round-2 additions (2026-06-15)
 * ---------------------------------------------------------------------------
 * Round 1 tokenised the brand/semantic core. Round 2 finishes the job across
 * the remaining .tsx/.ts and the .scss layer. Consolidations stay within the
 * SAME <= 2/255 imperceptibility rule:
 *   BLUE -> brandBlue  also absorbs #0d47be (-1,+1,+2) and #0d46bc (-1,0,0).
 * Colours that are PERCEPTIBLY different blues/greens/reds were NOT folded into
 * a brand token (that would be a visible change). They got their OWN named
 * tokens instead, because each recurs:
 *   accentBlue #2f54eb, accentBlueBright #335cff, vividBlue #0033ff (stepper /
 *   yudu-button / fill-up accents — 4/3/2 uses), eventBlue #1976d2 (default
 *   calendar event), and the helper.ts status set statusRejected #e53947 /
 *   statusNeutral #6b7280 / statusPurple #9c27b0, plus statusError #ff0101.
 * A small neutral GRAY scale was added (grayMuted #6c757d, grayLight #ccc,
 * grayMid #666, grayDark #333, grayBreadcrumb #abacaa, grayTrack #e9e9ea,
 * grayHairline #e5e5e5, grayBorderSoft #ddd) to absorb the scattered scss/sx
 * greys onto named handles.
 *
 * Genuinely one-off, intentionally-distinct colour ARRAYS are left as raw hex
 * (and allowlisted in the color-ratchet): the RichTextEditor font-colour picker
 * swatches, constant.ts ACCENT_COLORS / HIGHLIGHT_COLORS / PROGRESS_RANGE_COLORS,
 * and the KnowledgeGraph mastery-legend series. These are deliberate palettes,
 * not brand colours, so tokenising them would be wrong.
 *
 * How to use
 * ----------
 *   // sx / style object (TS):
 *   <Box sx={{ color: tokens.brandGreen }} />
 *   // with an alpha suffix (8-digit hex), use a template literal:
 *   <Box sx={{ bgcolor: `${tokens.brandGreen}26` }} />
 *   // in .scss / .css / styled template string:
 *   color: var(--color-brand-green);
 */
export const tokens = {
  // --- Brand (canonical of each near-dup cluster) ---
  brandGreen: "#02c995",
  brandYellow: "#fbd82c",
  brandBlue: "#0e46bc",

  // --- HG logo segment colours ---
  hgPurple: "#7B4EA8",
  hgOrange: "#F5921B",
  hgLimeGreen: "#80BC00",

  // --- Semantic ---
  primary: "#0e46bc", // == brandBlue (theme primary, was #0D46BC)
  secondary: "#bca700",
  success: "#4caf50", // MUI material green (theme success.main)
  error: "#ed1111", // app's bespoke error/rejected red (canonical)
  errorMaterial: "#f44336", // MUI material red (theme error.main + material-paired spots)
  warning: "#ff9800", // MUI material orange (theme warning.main)
  warningGold: "#b8860b", // darkgoldenrod — "for advisor approval" text
  warningYellow: "#f1c40f", // flat-ui yellow — warning status text in helper
  info: "#009cfe", // canonical info blue (helper status)
  infoSky: "#3cc1ff", // sky info (colors.primary, highlight)
  infoBlue: "#019afc", // alt info (appearance theme)
  infoMaterial: "#2196f3", // MUI material info (theme info.main)

  // --- Neutrals ---
  black: "#000000",
  white: "#ffffff",
  grayText: "#888888", // muted label / disabled text (== #888)
  grayBorder: "#d8d8d8",
  bgLight: "#f7f7f7", // light background.default
  paperDark: "#1a1a1a", // dark scheme paper

  // --- Neutral gray scale (round-2; consolidates scattered scss/sx grays) ---
  grayMuted: "#6c757d", // bootstrap-ish muted text (labels, helper text) — 6 uses
  grayLight: "#cccccc", // light border / disabled (== #ccc) — 7 uses
  grayMid: "#666666", // mid body text (== #666)
  grayDark: "#333333", // dark body text (== #333)
  grayBreadcrumb: "#abacaa", // breadcrumb muted text — 3 uses
  textDescription: "#4e4b66", // theme `description` typography colour
  grayTrack: "#e9e9ea", // MUI switch track / disabled track (theme)
  grayHairline: "#e5e5e5", // faint chart gridline / stroke
  grayBorderSoft: "#dddddd", // soft 1px border (== #ddd)
  grayPlaceholder: "#999999", // placeholder / fallback subject colour
  grayDisabled: "#e0e0e0", // disabled / no-data donut segment
  grayMuted2: "#9f9f9f", // muted text on filled pip
  grayEdge: "#bbbbbb", // knowledge-graph edge stroke (== #bbb)
  // scss-layer neutral/border greys (each perceptibly distinct — not folded into one)
  grayDarkSurface: "#343a40", // dark master-layout surface (bootstrap gray-800)
  grayCool: "#8c8c8c", // cool mid grey (stepper inactive text)
  grayStep: "#d9d9d9", // stepper connector / inactive border
  grayStepBg: "#e8e8e8", // stepper inactive circle bg
  grayInputBorder: "#dcdcdc", // disabled input border
  grayBorderFaint: "#eaeaea", // 1px faint table border
  bgPale: "#f8f9fa", // pale surface (bootstrap gray-100)
  bgDisabledInput: "#f0f0f0", // disabled input background

  // --- Accent blues (perceptibly distinct from brandBlue — NOT consolidated) ---
  accentBlue: "#2f54eb", // stepper active / step accent — 4 uses
  accentBlueBright: "#335cff", // bright fill-up / yudu-button accent — 3 uses
  vividBlue: "#0033ff", // yudu-button hover blue — 2 uses
  bootstrapBlue: "#007bff", // bootstrap primary link blue (master-layout)
  linkBlue: "#009dff", // journey-table link blue
  loadingBlue: "#0b48c0", // loading-screen text blue
  eventBlue: "#1976d2", // default calendar-event colour (MUI blue 700)
  accentYellow: "#ffd92e", // bright accent yellow (dropzone idle border) — distinct from brandYellow
  badgeOrange: "#fdb06a", // warm-orange card-type badge background

  // --- Mastery bands (PT/teacher assessment 90% verification: green >=90,
  //     yellow 70-89, red <70). Named after the pedagogical concept so the band
  //     intent is self-documenting and can be restyled in one place. Hues are the
  //     app's existing success/warning/error family, kept as their own handles so
  //     a future mastery-specific palette change does not disturb generic status. ---
  masteryMastered: "#2e7d32", // >=90% — mastered (MUI green 800, strong pass)
  masteryApproaching: "#ed6c02", // 70-89% — approaching mastery (MUI orange 800)
  masteryBelow: "#d32f2f", // <70% — below mastery (MUI red 700)

  // --- Status set (helper.ts assessment-status pills; recur with alpha tints) ---
  statusRejected: "#e53947", // rejected pill text
  statusNeutral: "#6b7280", // neutral/closed pill text
  statusPurple: "#9c27b0", // purple status pill text (== MUI purple 500)
  statusError: "#ff0101", // hard validation-error red (scss inputs)
  errorNotif: "#dc4747", // notification error background
  checkboxGold: "#f3cc35", // checked-checkbox gold fill/border

  // --- Surfaces / soft backgrounds ---
  bgInfoSoft: "#f2f9ff", // pale-blue info row background
  surfaceMuted: "#e2e4e9", // muted panel background (used at 0x33 alpha)
  bgAmberSoft: "#fff8e1", // internal-note amber row background
  pipPeachLight: "#ffe7d3", // unselected answer-pip background
  pipPeach: "#f4b373", // selected answer-pip background

  // --- Soft tints (status pill backgrounds) ---
  softRed: "#ffc5c2", // late / overdue pill bg
  softRedTint: "#fad2d2", // delete-confirm icon bg
  softGreen: "#ddf5d0", // on-track pill bg
  softBrandBlue: "#eaf0fb", // brand-blue tint, pill/chip background (PT dashboard grading summary)

  // ---------------------------------------------------------------------------
  // Orbit design language (2026-08-02)
  // ---------------------------------------------------------------------------
  // Light theme = "Orbit"; dark theme = "Deepfield". All values defined ONCE
  // here and imported by tutor/theme.ts via template literals so no additional
  // raw hex literals accumulate in the ratchet.
  //
  // Light surface
  orbitCanvas: "#F2F3F7",
  orbitSurface: "#FFFFFF",
  orbitBorder: "#E7E9F0",
  // Light ink
  orbitInk: "#0B0D12",
  orbitInkSecondary: "#5D6472",
  orbitInkTertiary: "#9AA1B1",
  // Light accent
  orbitAccent: "#5B4BF5",
  orbitAccentBright: "#8E7CFF",
  orbitAccentSoft: "#EEECFE",
  orbitAccentDeep: "#3D2FD6",
  // Light state colours
  orbitMastered: "#0FB89A",
  orbitShaky: "#E0A33A",
  orbitNotStarted: "#C9CEDA",
  // Aurora washes (used in gradient literals in theme.ts)
  orbitAuroraViolet: "#7A6BFF",
  orbitAuroraTeal: "#5FD8C4",
  // Deepfield (dark) surface
  orbitDeepBg: "#080A12",
  orbitDeepInk: "#EDF1F8",
  orbitDeepTeal: "#35E0D4",
  orbitDeepViolet: "#7C6BFF",
  // Deepfield ink scale
  orbitDeepText2: "#99A1B8",
  orbitDeepText3: "#5C6479",
  orbitDeepLine: "#1E2334",
  orbitDeepGold: "#F0A23B",
  orbitDeepOnTeal: "#04211F",
} as const;

export type TokenName = keyof typeof tokens;

/**
 * Spacing scale (round-2 SEED — establishes the pattern, not yet fully adopted).
 * ============================================================================
 * A clean 4px-based scale. The codebase today hardcodes ~314 inline
 * `padding`/`margin` literals (mostly 4/6/8/10/16/20/22/24px — several off-grid).
 * Rather than migrate all of them at once, this seeds the canonical scale and a
 * handful of components adopt it (see styles/README.md). Use `space.*` in TS/sx
 * (numbers, so usable as MUI `sx` spacing or raw px) and `var(--space-N)` in scss.
 *
 *   <Box sx={{ p: `${space[4]}px`, gap: `${space[2]}px` }} />
 */
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 32,
  7: 48,
  8: 64,
} as const;

export type SpaceStep = keyof typeof space;

/**
 * Typography scale (round-2 SEED).
 * ================================
 * Font sizes drawn from the values actually common in the codebase
 * (16px dominates; 13/14/15/18/20/24 follow) and the MUI theme variants.
 * Weights are the five real weights in use (400/500/600/700/800).
 * Use `fontSize.*` / `fontWeight.*` in TS/sx and `var(--font-size-*)` /
 * `var(--font-weight-*)` in scss.
 */
export const fontSize = {
  xs: "12px",
  sm: "13px",
  base: "14px", // theme typography.fontSize === 14
  md: "15px",
  lg: "16px", // most common inline size
  xl: "18px",
  "2xl": "20px",
  "3xl": "24px",
  "4xl": "28px", // page H1 (PageHeader) — must out-rank section headers (2xl/20px)
} as const;

export type FontSizeName = keyof typeof fontSize;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

export type FontWeightName = keyof typeof fontWeight;
